import { useEffect, useRef, useState } from 'react';
import {
  digitsOf,
  isJobBoundSource,
  type CloseMethod,
  type DraftChatResponse,
  type StartChatTarget,
} from '@chat-emp/shared';

import { Composer, type ComposerHandle } from '../conversation/Composer.js';
import { ConversationHeader } from '../conversation/ConversationHeader.js';
import { HiringStatusPill } from '../conversation/HiringStatusPill.js';
import { StageBar } from '../conversation/StageBar.js';
import { TemplatePanel } from '../conversation/TemplatePanel.js';
import { trackHiringStage } from '../conversation/hiringEvents.js';
import { useTemplates } from '../conversation/useTemplates.js';
import { Icon } from '../components/Icon.js';
import type { ApplicationStatus, Participant, ParticipantId } from '../fixtures/domain.js';
import type { HiringStage } from '../fixtures/rules.js';
import { emitToHost } from '../lib/bridge.js';
import { track } from '../lib/analytics.js';
import { draftChatApi, setDraftStatusApi, startChatApi } from '../lib/facade.js';
import { useDismiss } from '../util/useDismiss.js';
import { announceStarted } from './announceStarted.js';
import headerStyles from './WidgetHeader.module.css';
import panelStyles from './WidgetPanel.module.css';
import styles from './WidgetThread.module.css';

interface DraftThreadProps {
  requestId: number;
  target: StartChatTarget;
  /** The host's suggested opener. */
  draft?: string;
  /** A chat exists — found on open, or just created by a send. */
  onOpen: (channelId: string) => void;
  onClose: (method: CloseMethod) => void;
}

/**
 * The pane for an applicant with no chat yet: the thread's layout, empty.
 * Nothing here touches Stream, so opening it costs no MAU; the first send creates
 * the chat and hands over to the live thread.
 */
export function DraftThread({ requestId, target, draft, onOpen, onClose }: DraftThreadProps) {
  const [info, setInfo] = useState<DraftChatResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState<ApplicationStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const templates = useTemplates();
  const composerRef = useRef<ComposerHandle>(null);
  const job = isJobBoundSource(target.source) ? target.job : undefined;

  useDismiss(!templatesOpen, () => onClose('esc'));

  useEffect(() => {
    let live = true;
    draftChatApi({
      applicant: { id: target.applicant.id },
      source: target.source,
      ...(target.job ? { job: target.job } : {}),
    })
      .then((res) => {
        if (!live) return;
        if (res.channelId) return onOpen(res.channelId);
        const s = res.application?.status;
        if (s) setStatus(s === 'new' ? 'pending' : s);
        setInfo(res);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [requestId]);

  useEffect(() => {
    if (info && draft) composerRef.current?.insert(draft);
  }, [info]);

  const counterpart: Participant = {
    id: `js_${digitsOf(target.applicant.id)}` as ParticipantId,
    name: target.applicant.name,
    role: 'jobseeker',
    ...(target.applicant.image ? { avatarUrl: target.applicant.image } : {}),
    ...(target.applicant.locked ? { locked: true } : {}),
  };

  const send = async (text: string) => {
    const { channelId } = await startChatApi({
      applicant: target.applicant,
      company: target.company,
      source: target.source,
      ...(target.sourceMeta ? { sourceMeta: target.sourceMeta } : {}),
      ...(target.job ? { job: target.job } : {}),
      text,
    });
    announceStarted(requestId, target, channelId);
    onOpen(channelId);
  };

  const write = async (next: HiringStage | 'rejected') => {
    if (!job || !status) return;
    const previous = status;
    setWriteError(null);
    setSaving(true);
    setStatus(next);
    try {
      await setDraftStatusApi({ applicant: { id: target.applicant.id }, job }, next);
      if (next === 'rejected') onClose('panel-header');
    } catch {
      setStatus(previous);
      setWriteError(
        next === 'rejected'
          ? 'Could not reject the applicant.'
          : 'Could not update the hiring stage.',
      );
    } finally {
      setSaving(false);
    }
  };

  const markStage = (stage: HiringStage) => {
    trackHiringStage({ stage, surface: 'pane', via: 'button' });
    void write(stage);
  };

  const viewProfile = () => {
    track('profile_opened', { surface: 'pane' });
    emitToHost('view-profile', { jobseekerId: digitsOf(target.applicant.id) });
    if (document.documentElement.dataset.viewport === 'mobile') onClose('panel-header');
  };

  const rejected = status === 'rejected';

  return (
    <div className={panelStyles.panel} role="dialog" aria-label="Chats">
      <div className={styles.head} data-drag-handle>
        <ConversationHeader
          counterpart={counterpart}
          jobTitle={info?.jobTitle}
          stage={job && status && !rejected ? status : undefined}
          actions={
            <>
              {job && rejected && <HiringStatusPill status="rejected" compact />}
              <button
                className={headerStyles.close}
                onClick={() => onClose('panel-header')}
                aria-label="Close chat"
              >
                <Icon name="close" size={17} />
              </button>
            </>
          }
          compact
          detailsOpen={false}
          onToggleDetails={viewProfile}
        />
      </div>
      {job && status && !rejected && (
        <StageBar
          status={status}
          busy={saving}
          onStage={markStage}
          onReject={() => void write('rejected')}
          compact
          rejectOnly
        />
      )}
      {(writeError || failed) && (
        <p className={styles.writeError} role="alert">
          {writeError ?? 'Could not load this applicant.'} Please try again.
        </p>
      )}
      <div className={styles.messages}>
        {!info && !failed && <p className={panelStyles.opening}>Opening chat…</p>}
      </div>
      <Composer
        ref={composerRef}
        onSend={send}
        disabled={!info}
        placeholder={`Write to ${target.applicant.name}…`}
        noExpand
        onTemplates={() => {
          emitToHost('template-click', {});
          setTemplatesOpen(true);
        }}
      />
      {templatesOpen && (
        <TemplatePanel
          templates={templates.templates}
          loading={templates.loading}
          error={templates.error}
          canCreateMore={templates.canCreateMore}
          onFetch={templates.fetch}
          context={{ candidateName: target.applicant.name, jobTitle: info?.jobTitle }}
          onUse={(t, text) => {
            emitToHost('template-use', { id: t.id, name: t.name, template: t.template });
            track('template_used', { template_id: t.id });
            composerRef.current?.insert(text);
          }}
          onCreate={templates.create}
          onUpdate={templates.update}
          onDelete={templates.remove}
          onClose={() => setTemplatesOpen(false)}
        />
      )}
    </div>
  );
}
